/**
 * Página de gerenciamento de transações financeiras
 * 
 * Esta página é o coração do sistema de controle financeiro, permitindo
 * visualizar, filtrar, criar, editar e excluir transações. Suporta diferentes
 * tipos de transações: receitas, despesas, transferências, parcelamentos
 * e recorrências. Inclui sistema de filtros avançados e interface intuitiva.
 * 
 * @author Amazon Q Developer
 * @since 1.0.0
 */

import { LitElement, html } from 'lit'
import '../components/floating-button.js'
import '../components/modal-transacao.js'
import '../components/modal-parcelas.js'
import '../components/modal-recorrencias.js'
import '../components/modal-confirm.js'
import '../components/premium-guard.js'
import '../components/seletor-periodo.js'
import { contaCacheService } from '../core/services/conta-cache.service.js'
import { responsavelCacheService } from '../core/services/responsavel-cache.service.js'
import { categoriaCacheService } from '../core/services/categoria-cache.service.js'
import { tagCacheService } from '../core/services/tag-cache.service.js'
import { transacaoCacheService } from '../core/services/transacao-cache.service.js'
import { cacheService } from '../core/services/cache.service.js'
import { ProtectedPageMixin } from '../core/mixins/protected-page.mixin.js'
import { calcularStatusLocal } from '../core/utils/status-transacao.util.js'

/**
 * Componente principal da página de transações
 * 
 * Gerencia todas as operações relacionadas a transações financeiras,
 * incluindo listagem, filtros, CRUD e gerenciamento de parcelas/recorrências.
 */
class Transacoes extends ProtectedPageMixin(LitElement) {
  static properties = {
    transactions: { type: Array },
    filteredTransactions: { type: Array },
    filters: { type: Object },
    loading: { type: Boolean },
    contas: { type: Array },
    categorias: { type: Array },
    responsaveis: { type: Array },
    tags: { type: Array },
    expandedTransactions: { type: Set },
    currentFilterMonth: { type: Number },
    currentFilterYear: { type: Number },
    authError: { type: String }
  }

  /**
   * Mantém os estilos do Bulma CSS framework
   * 
   * Retorna o próprio elemento em vez de criar um Shadow DOM,
   * permitindo que os estilos do Bulma sejam aplicados diretamente.
   * 
   * @returns {HTMLElement} O próprio elemento para manter estilos globais
   */
  createRenderRoot() {
    return this // manter estilos do Bulma
  }

  /**
   * Inicializa o componente da página de transações
   * 
   * Define estado inicial dos filtros, arrays de dados e configura
   * listeners para atualizações automáticas quando dados são modificados
   * em outras partes da aplicação.
   */
  constructor() {
    super()
    // Filtros padrão - período atual
    const hoje = new Date()
    const meses = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro']
    const mesAtual = meses[hoje.getMonth()]
    const anoAtual = hoje.getFullYear()
    
    this.filters = {
      periodo: `${mesAtual} de ${anoAtual}`,
      conta: 'Todas as Contas',
      categoria: 'Todas as Categorias',
      tag: 'Todas as Tags'
    }
    this.transactions = []
    this.filteredTransactions = []
    this.loading = false
    this.contas = []
    this.categorias = []
    this.responsaveis = []
    this.tags = []
    this.expandedTransactions = new Set()
    this.currentFilterMonth = hoje.getMonth()
    this.currentFilterYear = hoje.getFullYear()
    /** @type {string|null} Mensagem de erro de autenticação (timeout ou falha) */
    this.authError = null
    
    // Inicializar serviços com cache inteligente
    this.transacaoService = transacaoCacheService
    this.contaCacheService = contaCacheService
    this.categoriaCacheService = categoriaCacheService
    this.responsavelCacheService = responsavelCacheService
    this.tagCacheService = tagCacheService

    // Bind dos handlers de cache para remoção correta no disconnectedCallback
    this._boundHandleTransacoesCacheUpdated = this._handleTransacoesCacheUpdated.bind(this)
    this._boundHandleContasCacheUpdated = this._handleContasCacheUpdated.bind(this)
    this._boundHandleCategoriasCacheUpdated = this._handleCategoriasCacheUpdated.bind(this)
    this._boundHandleTagsCacheUpdated = this._handleTagsCacheUpdated.bind(this)
  }

  /**
   * Conecta componente ao DOM, registra listeners de cache e inicia carregamento de dados
   * protegido pelo ProtectedPageMixin (aguarda autenticação).
   * 
   * Registra listeners de eventos de cache para atualização reativa da UI
   * sem necessidade de GET redundante ao backend.
   */
  connectedCallback () {
    super.connectedCallback()

    // Listeners de eventos de cache para atualização reativa da UI
    window.addEventListener('transacoes-cache-updated', this._boundHandleTransacoesCacheUpdated)
    window.addEventListener('contas-cache-updated', this._boundHandleContasCacheUpdated)
    window.addEventListener('categorias-cache-updated', this._boundHandleCategoriasCacheUpdated)
    window.addEventListener('tags-cache-updated', this._boundHandleTagsCacheUpdated)

    this._loadWithAuthGuard(async () => {
      await this._loadData()
    })
  }

  /**
   * Remove listeners de eventos de cache ao desconectar o componente do DOM
   * Previne memory leaks e chamadas a componentes destruídos
   */
  disconnectedCallback () {
    window.removeEventListener('transacoes-cache-updated', this._boundHandleTransacoesCacheUpdated)
    window.removeEventListener('contas-cache-updated', this._boundHandleContasCacheUpdated)
    window.removeEventListener('categorias-cache-updated', this._boundHandleCategoriasCacheUpdated)
    window.removeEventListener('tags-cache-updated', this._boundHandleTagsCacheUpdated)
    super.disconnectedCallback()
  }

  /**
   * Manipula evento transacoes-cache-updated lendo dados do cache de forma síncrona
   * Busca transações do período atualmente filtrado no cache local.
   * Implementa guard contra null/undefined para manter valor atual da propriedade.
   *
   * @private
   */
  _handleTransacoesCacheUpdated () {
    const mes = String(this.currentFilterMonth + 1).padStart(2, '0')
    const ano = String(this.currentFilterYear)
    const cacheKey = `transacoes_${mes}_${ano}`

    const transacoes = cacheService.get(cacheKey)
    if (transacoes != null) {
      this.transactions = transacoes
      this._applyFilters()
      this.requestUpdate()
    }
  }

  /**
   * Manipula evento contas-cache-updated lendo dados do cache de forma síncrona
   * Implementa guard contra null/undefined para manter valor atual.
   *
   * @private
   */
  _handleContasCacheUpdated () {
    const contas = cacheService.get('contas')
    if (contas != null) {
      this.contas = contas
      this.requestUpdate()
    }
  }

  /**
   * Manipula evento categorias-cache-updated lendo dados do cache de forma síncrona
   * Implementa guard contra null/undefined para manter valor atual.
   *
   * @private
   */
  _handleCategoriasCacheUpdated () {
    const categorias = cacheService.get('categorias')
    if (categorias != null) {
      this.categorias = categorias
      this.requestUpdate()
    }
  }

  /**
   * Manipula evento tags-cache-updated lendo dados do cache de forma síncrona
   * Implementa guard contra null/undefined para manter valor atual.
   *
   * @private
   */
  _handleTagsCacheUpdated () {
    const tags = cacheService.get('tags')
    if (tags != null) {
      this.tags = tags
      this.requestUpdate()
    }
  }

  /**
   * Carrega todos os dados necessários para a página
   * 
   * Busca transações, contas, categorias, responsáveis e tags
   * em paralelo para otimizar performance. Aplica filtros
   * após carregamento completo.
   * A espera pelo auth é gerenciada pelo ProtectedPageMixin via _loadWithAuthGuard.
   * 
   * @private
   */
  async _loadData() {
    this.loading = true
    try {
      // Carregar períodos iniciais de transações (anterior, atual, próximo)
      await this.transacaoService.listar()
      
      const [contas, categorias, responsaveis, tags] = await Promise.all([
        this.contaCacheService.listar(),
        this.categoriaCacheService.listar(),
        this.responsavelCacheService.listar(),
        this.tagCacheService.listar()
      ])
      
      this.contas = contas
      this.categorias = categorias
      this.responsaveis = responsaveis
      this.tags = tags
      
      // Aplicar filtro para carregar transações do período atual
      await this._applyFilters()
    } catch (error) {
      console.error('❌ Erro ao carregar dados:', error)
    } finally {
      this.loading = false
    }
  }

  async _applyFilters() {
    // Busca transações do período selecionado
    if (this.filters.periodo) {
      const partes = this.filters.periodo.split(' de ')
      
      if (partes.length === 2) {
        const [mes, ano] = partes
        const meses = {
          'janeiro': '01', 'fevereiro': '02', 'março': '03', 'abril': '04',
          'maio': '05', 'junho': '06', 'julho': '07', 'agosto': '08',
          'setembro': '09', 'outubro': '10', 'novembro': '11', 'dezembro': '12'
        }
        const mesNum = meses[mes.toLowerCase()]
        
        if (mesNum) {
          this.transactions = await this.transacaoService.listarPorPeriodo(mesNum, ano)
        }
      }
    }

    let filtered = [...this.transactions]

    if (this.filters.conta !== 'Todas as Contas') {
      const conta = this.contas.find(c => c.nome === this.filters.conta)
      if (conta) {
        filtered = filtered.filter(t => t.contaId === conta.id)
      }
    }

    if (this.filters.categoria !== 'Todas as Categorias') {
      const categoria = this.categorias.find(c => `${c.emoji} ${c.nome}` === this.filters.categoria)
      if (categoria) {
        filtered = filtered.filter(t => t.categoriaId === categoria.id)
      }
    }

    if (this.filters.tag !== 'Todas as Tags') {
      filtered = filtered.filter(t => t.tagNomes && t.tagNomes.includes(this.filters.tag))
    }

    this.filteredTransactions = filtered
  }

  /**
   * Exclui transação após confirmação
   * 
   * Solicita confirmação do usuário através de modal e,
   * se confirmado, exclui a transação via serviço.
   * 
   * @param {string} transactionId - ID da transação a ser excluída
   * @private
   */
  async _deleteTransaction(transactionId) {
    const confirmed = await new Promise((resolve) => {
      const modal = document.createElement('modal-confirm')
      modal.show(
        'Tem certeza que deseja excluir esta transação?', 
        'Confirmar Exclusão', 
        'Excluir', 
        'Cancelar', 
        'danger'
      )
      
      const handleConfirm = () => {
        modal.removeEventListener('modal-confirmed', handleConfirm)
        modal.removeEventListener('modal-cancelled', handleCancel)
        document.body.removeChild(modal)
        resolve(true)
      }
      
      const handleCancel = () => {
        modal.removeEventListener('modal-confirmed', handleConfirm)
        modal.removeEventListener('modal-cancelled', handleCancel)
        document.body.removeChild(modal)
        resolve(false)
      }
      
      modal.addEventListener('modal-confirmed', handleConfirm)
      modal.addEventListener('modal-cancelled', handleCancel)
      document.body.appendChild(modal)
    })
    
    if (confirmed) {
      try {
        await this.transacaoService.excluir(transactionId)
        await this._loadData()
      } catch (error) {
        console.error('Erro ao excluir transação:', error)
        alert('Erro ao excluir transação. Tente novamente.')
      }
    }
  }

  /**
   * Formata valores monetários para exibição
   * 
   * Converte números em formato de moeda brasileira (BRL)
   * seguindo padrões de localização do Brasil.
   * 
   * @param {number} amount - Valor a ser formatado
   * @returns {string} Valor formatado como moeda (ex: "R$ 1.234,56")
   * @private
   */
  _formatCurrency(amount) {
    return amount.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    })
  }

  /**
   * Retorna classe CSS baseada no sinal do valor
   * 
   * Valores positivos (receitas) recebem classe verde,
   * valores negativos (despesas) recebem classe vermelha.
   * Transferências usam classe azul (neutra).
   * 
   * @param {number} amount - Valor da transação
   * @param {Object} [transaction] - Transação para verificar tipo
   * @returns {string} Classe CSS do Bulma para coloração
   * @private
   */
  _getAmountClass(amount, transaction) {
    if (transaction?.tipo === 'transferencia') return 'has-text-info'
    return amount >= 0 ? 'has-text-success' : 'has-text-danger'
  }

  /**
   * Formata valor com sinal apropriado
   * 
   * Formata o valor como moeda e adiciona sinal de menos
   * para valores negativos, mantendo formatação consistente.
   * Transferências exibem valor sem sinal (neutro).
   * 
   * @param {number} amount - Valor a ser formatado
   * @param {Object} [transaction] - Transação para verificar tipo
   * @returns {string} Valor formatado com sinal (ex: "-R$ 100,00")
   * @private
   */
  _formatAmount(amount, transaction) {
    if (transaction?.tipo === 'transferencia') {
      return this._formatCurrency(Math.abs(amount))
    }
    const formatted = this._formatCurrency(Math.abs(amount))
    return amount >= 0 ? formatted : `-${formatted}`
  }



  /**
   * Renderiza badge visual de status da transação
   *
   * Calcula o status localmente usando calcularStatusLocal e exibe um badge
   * colorido conforme o estado: verde para paga/recebida/efetivada, amarelo
   * para a_vencer e vermelho para atrasada. Utiliza tokens CSS do design system.
   *
   * @param {Object} transacao - Transação com campos dataPagamento, dataVencimento, tipo
   * @returns {import('lit').TemplateResult|string} Badge HTML ou string vazia se status indefinido
   * @private
   */
  _renderStatusBadge (transacao) {
    const status = calcularStatusLocal(transacao)
    const config = {
      paga: { texto: 'Paga', cor: 'var(--success, #35D319)' },
      recebida: { texto: 'Recebida', cor: 'var(--success, #35D319)' },
      efetivada: { texto: 'Efetivada', cor: 'var(--success, #35D319)' },
      a_vencer: { texto: 'A vencer', cor: 'var(--warning, #FFB84D)' },
      atrasada: { texto: 'Atrasada', cor: 'var(--danger, #EF4444)' }
    }
    const { texto, cor } = config[status] || {}
    if (!texto) return ''
    return html`
      <span class="tag is-small" style="background-color: ${cor}; color: white;"
            data-cy="badge-status-${status}">
        ${texto}
      </span>
    `
  }

  /**
   * Manipula mudanças nos filtros
   * 
   * Atualiza o estado dos filtros quando usuário seleciona
   * nova opção e reaplica filtros automaticamente.
   * 
   * @param {string} filterType - Tipo do filtro (periodo, conta, categoria, tag)
   * @param {string} value - Novo valor selecionado
   * @private
   */
  async _onFilterChange(filterType, value) {
    this.filters = { ...this.filters, [filterType]: value }
    await this._applyFilters()
    this.requestUpdate()
  }

  /**
   * Manipula evento periodo-changed emitido pelo componente seletor-periodo
   * 
   * Extrai mês (0-11) e ano do event.detail e converte para o formato
   * "mês de ano" utilizado pelo filtro de transações. Aplica o filtro de
   * período mantendo os demais filtros (conta, categoria, tag) inalterados.
   * 
   * @param {CustomEvent} event - Evento com detail: { mes: Number(0-11), ano: Number }
   * @private
   */
  _onPeriodoChanged(event) {
    const { mes, ano } = event.detail
    this.currentFilterMonth = mes
    this.currentFilterYear = ano
    const meses = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro']
    const newPeriodo = `${meses[mes]} de ${ano}`
    this._onFilterChange('periodo', newPeriodo)
  }

  /**
   * Agrupa transações por data de vencimento (ou data de lançamento como fallback)
   * 
   * Organiza as transações filtradas em grupos por dataVencimento (ou campo data
   * quando dataVencimento é nulo) para exibição em formato de extrato cronológico.
   * Ordena os grupos por data decrescente (mais recentes primeiro).
   *
   * Utiliza dataVencimento como chave primária de agrupamento conforme Requirement 15.1,
   * com fallback para campo data quando dataVencimento é nulo (Requirement 15.2).
   * 
   * @returns {Object} Objeto com datas como chaves e arrays de transações como valores, ordenado por data
   * @private
   */
  _groupTransactionsByDate() {
    const grouped = {}

    // Ordena transações por dataVencimento (fallback: data) antes de agrupar
    const sorted = [...this.filteredTransactions].sort((a, b) => {
      const dataA = a.dataVencimento || a.data
      const dataB = b.dataVencimento || b.data
      if (dataA < dataB) return -1
      if (dataA > dataB) return 1
      return 0
    })

    sorted.forEach(transaction => {
      const dataAgrupamento = transaction.dataVencimento || transaction.data
      if (!grouped[dataAgrupamento]) {
        grouped[dataAgrupamento] = []
      }
      grouped[dataAgrupamento].push(transaction)
    })
    return grouped
  }

  /**
   * Abre modal para adicionar nova transação
   * 
   * Acionado pelo botão flutuante, abre o modal de transação
   * em modo de criação (sem dados pré-preenchidos).
   * 
   * @private
   */
  _handleAddTransaction() {
    const modal = this.renderRoot.querySelector('modal-transacao')
    modal.show()
  }

  /**
   * Abre modal para editar transação existente
   * 
   * Localiza a transação pelo ID e abre o modal de transação
   * em modo de edição com dados pré-preenchidos.
   * 
   * @param {string} transactionId - ID da transação a ser editada
   * @private
   */
  _editTransaction(transactionId) {
    const transaction = this.transactions.find(t => t.id === transactionId)
    if (transaction) {
      const modal = this.renderRoot.querySelector('modal-transacao')
      modal.show(transaction)
    }
  }

  _manageInstallmentRecurring(transactionId) {
    const transaction = this.transactions.find(t => t.id === transactionId)
    if (!transaction) return

    if (transaction.installment) {
      const modalParcelas = this.renderRoot.querySelector('modal-parcelas')
      modalParcelas.show(transaction)
    } else if (transaction.recurring) {
      const modalRecorrencias = this.renderRoot.querySelector('modal-recorrencias')
      modalRecorrencias.show(transaction)
    }
  }

  _handleEditInstallment(event) {
    const { transaction } = event.detail
    const modal = this.renderRoot.querySelector('modal-transacao')
    modal.show(transaction, 'modal-parcelas')
  }

  _handleDeleteInstallment(event) {
    const { transactionId, installmentNumber } = event.detail
    console.log(`Excluir parcela ${installmentNumber} da transação ${transactionId}`)
    // Aqui você implementaria a lógica para excluir a parcela específica
  }

  _handleDeleteAllInstallments(event) {
    const { transactionId } = event.detail
    console.log(`Excluir todas as parcelas da transação ${transactionId}`)
    // Aqui você implementaria a lógica para excluir todas as parcelas
  }

  _handleEditOccurrence(event) {
    const { transaction } = event.detail
    const modal = this.renderRoot.querySelector('modal-transacao')
    modal.show(transaction, 'modal-recorrencias')
  }

  _handleDeleteOccurrence(event) {
    const { transactionId, occurrenceNumber } = event.detail
    console.log(`Excluir ocorrência ${occurrenceNumber} da transação ${transactionId}`)
  }

  _handleDeleteAllOccurrences(event) {
    const { transactionId } = event.detail
    console.log(`Excluir toda a série da transação ${transactionId}`)
  }

  _handleReturnToCaller(event) {
    const { callerModal, transaction } = event.detail
    
    if (callerModal === 'modal-parcelas') {
      const modalParcelas = this.renderRoot.querySelector('modal-parcelas')
      modalParcelas.show(transaction)
    } else if (callerModal === 'modal-recorrencias') {
      const modalRecorrencias = this.renderRoot.querySelector('modal-recorrencias')
      modalRecorrencias.show(transaction)
    }
  }

  /**
   * Monta a descrição complementar para transferências
   * 
   * Busca os nomes das contas de origem e destino a partir do cache
   * e retorna no formato "Conta Origem → Conta Destino".
   * 
   * @param {Object} transaction - Transação do tipo transferência
   * @returns {string} Descrição no formato "Origem → Destino"
   * @private
   */
  _getTransferenciaDescricao (transaction) {
    const origemId = transaction.infoTransferencia?.contaOrigemId
    const destinoId = transaction.infoTransferencia?.contaDestinoId
    const nomeOrigem = this._getContaNome(origemId)
    const nomeDestino = this._getContaNome(destinoId)
    return `${nomeOrigem} → ${nomeDestino}`
  }

  _getContaNome(contaId) {
    const conta = this.contas.find(c => c.id === contaId)
    return conta?.nome || 'Conta não encontrada'
  }

  _getCategoriaNome(categoriaId) {
    const categoria = this.categorias.find(c => c.id === categoriaId)
    return categoria ? `${categoria.emoji} ${categoria.nome}` : 'Categoria não encontrada'
  }

  _getResponsavelNome(responsavelId) {
    if (!responsavelId) return ''
    const responsavel = this.responsaveis.find(r => r.id === responsavelId)
    return responsavel ? (responsavel.nomeExibicao || responsavel.nome) : ''
  }

  /**
   * Alterna expansão de transação compartilhada
   * Mostra/oculta detalhes das divisões por responsável
   * @param {string} transactionId - ID da transação
   * @private
   */
  _toggleTransactionExpansion(transactionId) {
    if (this.expandedTransactions.has(transactionId)) {
      this.expandedTransactions.delete(transactionId)
    } else {
      this.expandedTransactions.add(transactionId)
    }
    this.requestUpdate()
  }

  /**
   * Verifica se transação está expandida
   * @param {string} transactionId - ID da transação
   * @returns {boolean} True se expandida
   * @private
   */
  _isTransactionExpanded(transactionId) {
    return this.expandedTransactions.has(transactionId)
  }

  render() {
    const groupedTransactions = this._groupTransactionsByDate()

    return html`
      <premium-guard>
        <section class="section">
        <div class="container" style="padding-bottom: 90px;">
          <div class="is-flex is-justify-content-space-between is-align-items-center mb-5">
            <h1 class="title is-4" data-cy="transacoes-title">Transações</h1>
          </div>

          <!-- Card de Filtros -->
          <div class="card mb-5" data-cy="card-filtros">
            <header class="card-header">
              <p class="card-header-title">Filtros</p>
            </header>
            <div class="card-content">
              <div class="columns is-multiline">
                <div class="column is-3">
                  <div class="field">
                    <label class="label is-size-7 has-text-weight-medium">Período</label>
                    <div class="control">
                      <seletor-periodo
                        .mes="${this.currentFilterMonth}"
                        .ano="${this.currentFilterYear}"
                        @periodo-changed="${this._onPeriodoChanged}"
                      ></seletor-periodo>
                    </div>
                  </div>
                </div>
                <div class="column is-3">
                  <div class="field">
                    <label class="label is-size-7 has-text-weight-medium">Conta</label>
                    <div class="control">
                      <div class="select is-fullwidth">
                        <select 
                          data-cy="filter-conta"
                          @change="${(e) => this._onFilterChange('conta', e.target.value)}"
                        >
                          <option>Todas as Contas</option>
                          ${this.contas.map(conta => html`
                            <option value="${conta.nome}">${conta.nome}</option>
                          `)}
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="column is-3">
                  <div class="field">
                    <label class="label is-size-7 has-text-weight-medium">Categoria</label>
                    <div class="control">
                      <div class="select is-fullwidth">
                        <select 
                          data-cy="filter-categoria"
                          @change="${(e) => this._onFilterChange('categoria', e.target.value)}"
                        >
                          <option>Todas as Categorias</option>
                          ${this.categorias.map(categoria => html`
                            <option value="${categoria.emoji} ${categoria.nome}">${categoria.emoji} ${categoria.nome}</option>
                          `)}
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="column is-3">
                  <div class="field">
                    <label class="label is-size-7 has-text-weight-medium">Tag</label>
                    <div class="control">
                      <div class="select is-fullwidth">
                        <select 
                          data-cy="filter-tag"
                          @change="${(e) => this._onFilterChange('tag', e.target.value)}"
                        >
                          <option>Todas as Tags</option>
                          ${this.tags.map(tag => html`
                            <option value="${tag.nome}">${tag.nome}</option>
                          `)}
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Card de Extrato -->
          <div class="card" data-cy="card-extrato">
            <header class="card-header">
              <p class="card-header-title">Extrato</p>
            </header>
            <div class="card-content">
              ${this.loading ? html`
                <div class="has-text-centered py-6">
                  <span class="icon is-large has-text-grey-light">
                    <i class="fas fa-spinner fa-pulse fa-2x"></i>
                  </span>
                  <p class="has-text-grey mt-3">Carregando transações...</p>
                </div>
              ` : Object.keys(groupedTransactions).length === 0 
                ? html`
                    <div class="has-text-centered py-6" data-cy="no-transactions">
                      <span class="icon is-large has-text-grey-light">
                        <i class="fas fa-receipt fa-3x"></i>
                      </span>
                      <p class="has-text-grey mt-4 mb-2">Nenhuma transação encontrada</p>
                      <p class="has-text-grey-light is-size-7">Adicione receitas, despesas ou transferências para começar</p>
                    </div>
                  `
                : Object.keys(groupedTransactions).map(date => html`
                <div class="mb-5">
                  <h3 class="has-text-grey mb-3" data-cy="transaction-date">${date}</h3>
                  <div class="space-y-2">
                    ${groupedTransactions[date].map(transaction => html`
                      <div 
                        class="box p-3" 
                        style="border: 1px solid var(--border, #26304A); border-radius: 8px;"
                        data-cy="transaction-item"
                      >
                        <div class="is-flex is-justify-content-space-between is-align-items-center">
                          <div class="is-flex-grow-1">
                            <div class="is-flex is-align-items-center is-flex-wrap-wrap mb-1" style="gap: 0.5rem;">
                              ${transaction.tipo === 'transferencia' ? html`
                                <span class="icon is-small has-text-info" data-cy="transfer-icon">
                                  <i class="fas fa-exchange-alt"></i>
                                </span>
                              ` : ''}
                              <span class="has-text-weight-medium" data-cy="transaction-description">
                                ${transaction.infoParcelas?.numeroParcelas ? `${transaction.descricao} (${transaction.infoParcelas.current || 1}/${transaction.infoParcelas.numeroParcelas})` : transaction.descricao}
                              </span>
                              ${transaction.recorrente ? html`
                                <span class="tag is-small is-info is-light" data-cy="recurring-badge">🔄 Recorrente</span>
                              ` : ''}
                              ${transaction.parcelado ? html`
                                <span class="tag is-small is-warning is-light" data-cy="installment-badge">${transaction.infoParcelas.current || 1}/${transaction.infoParcelas.numeroParcelas}</span>
                              ` : ''}
                              ${transaction.compartilhado ? html`
                                <span class="tag is-small is-success is-light" data-cy="shared-badge">👥 Compartilhado</span>
                              ` : ''}
                              ${this._renderStatusBadge(transaction)}
                            </div>
                            <div class="is-size-7 has-text-grey" data-cy="transaction-details">
                              ${transaction.tipo === 'transferencia'
                                ? html`<span data-cy="transfer-description">${this._getTransferenciaDescricao(transaction)}</span>`
                                : html`${this._getCategoriaNome(transaction.categoriaId)} • ${this._getContaNome(transaction.contaId)}${this._getResponsavelNome(transaction.responsavelId) ? ` • ${this._getResponsavelNome(transaction.responsavelId)}` : ''}`
                              }
                            ${transaction.tagNomes && transaction.tagNomes.length > 0 ? html`
                              <div class="tags mt-2">
                                ${transaction.tagNomes.map(tag => html`
                                  <span class="tag is-small is-primary is-light" data-cy="transaction-tag">${tag}</span>
                                `)}
                              </div>
                            ` : ''}
                          </div>
                          <div class="is-flex is-align-items-center" style="gap: 0.5rem;">
                            <span 
                              class="has-text-weight-medium ${this._getAmountClass(transaction.valor, transaction)}"
                              data-cy="transaction-amount"
                            >
                              ${this._formatAmount(transaction.valor, transaction)}
                            </span>
                            ${transaction.compartilhado && transaction.divisoes ? html`
                              <button 
                                class="button is-small is-ghost"
                                data-cy="btn-expand-transaction"
                                title="${this._isTransactionExpanded(transaction.id) ? 'Ocultar divisões' : 'Mostrar divisões'}"
                                @click="${() => this._toggleTransactionExpansion(transaction.id)}"
                              >
                                <span class="icon is-small">
                                  <i class="fas fa-chevron-${this._isTransactionExpanded(transaction.id) ? 'up' : 'down'}"></i>
                                </span>
                              </button>
                            ` : ''}
                            ${transaction.recorrente || transaction.parcelado ? html`
                              <button 
                                class="button is-small is-ghost"
                                data-cy="btn-manage-installment-recurring"
                                title="Gerenciar parcelas/recorrências"
                                @click="${() => this._manageInstallmentRecurring(transaction.id)}"
                              >
                                <span class="icon is-small">
                                  <i class="fas fa-list"></i>
                                </span>
                              </button>
                            ` : ''}
                            <button 
                              class="button is-small is-ghost"
                              data-cy="btn-edit-transaction"
                              title="Editar transação"
                              @click="${() => this._editTransaction(transaction.id)}"
                            >
                              <span class="icon is-small">
                                <i class="fas fa-pen"></i>
                              </span>
                            </button>
                            <button 
                              class="button is-small is-ghost"
                              data-cy="btn-delete-transaction"
                              title="Excluir transação"
                              @click="${() => this._deleteTransaction(transaction.id)}"
                            >
                              <span class="icon is-small">
                                <i class="fas fa-trash"></i>
                              </span>
                            </button>
                          </div>
                        </div>
                        
                        <!-- Divisões expandidas -->
                        ${transaction.compartilhado && transaction.divisoes && this._isTransactionExpanded(transaction.id) ? html`
                          <div class="mt-3 pt-3" style="border-top: 1px solid var(--border, #26304A);">
                            <p class="has-text-weight-medium mb-2 is-size-7">Divisão por responsável:</p>
                            ${transaction.divisoes.map(divisao => {
                              const responsavel = this.responsaveis.find(r => r.id === divisao.responsavelId)
                              const nomeResponsavel = responsavel ? (responsavel.nomeExibicao || responsavel.nome) : 'Desconhecido'
                              return html`
                                <div class="is-flex is-justify-content-space-between is-align-items-center py-1">
                                  <span class="is-size-7">${nomeResponsavel}</span>
                                  <div class="is-flex is-align-items-center" style="gap: 0.5rem;">
                                    <span class="is-size-7 has-text-grey">${divisao.percentual?.toFixed(1)}%</span>
                                    <span class="is-size-7 has-text-weight-medium ${this._getAmountClass(transaction.tipo === 'despesa' ? -Math.abs(divisao.valor) : Math.abs(divisao.valor))}">
                                      ${this._formatAmount(transaction.tipo === 'despesa' ? -Math.abs(divisao.valor) : Math.abs(divisao.valor))}
                                    </span>
                                  </div>
                                </div>
                              `
                            })}
                          </div>
                        ` : ''}
                      </div>
                    `)}
                  </div>
                </div>
              `)}
            </div>
          </div>
        </div>

        <floating-button @floating-button-click="${this._handleAddTransaction}"></floating-button>
        <modal-transacao @return-to-caller="${this._handleReturnToCaller}"></modal-transacao>
        <modal-parcelas 
          @edit-installment="${this._handleEditInstallment}"
          @delete-installment="${this._handleDeleteInstallment}"
          @delete-all-installments="${this._handleDeleteAllInstallments}"
        ></modal-parcelas>
        <modal-recorrencias
          @edit-occurrence="${this._handleEditOccurrence}"
          @delete-occurrence="${this._handleDeleteOccurrence}"
          @delete-all-occurrences="${this._handleDeleteAllOccurrences}"
        ></modal-recorrencias>
        </section>
      </premium-guard>
    `
  }
}

customElements.define('transacoes-page', Transacoes)